import { useRef, useState } from 'react';

import { NavLink } from 'react-router-dom';

import {
  Avatar,
  Box,
  Button,
  Divider,
  Hidden,
  List,
  ListItem,
  ListItemText,
  Popover,
  Typography,
  CircularProgress,
  Tooltip
} from '@mui/material';

import { styled } from '@mui/material/styles';
import ExpandMoreTwoToneIcon from '@mui/icons-material/ExpandMoreTwoTone';
import LockOpenTwoToneIcon from '@mui/icons-material/LockOpenTwoTone';
import PersonIcon from '@mui/icons-material/Person';
import { useConfig } from 'src/core/config';
import { performLogout } from 'src/core/features/auth/authHelpers';
import { drHerancaBranding, drHerancaBrandingPresets } from 'src/drheranca/branding';

const UserBoxButton = styled(Button)(
  ({ theme }) => `
        padding-left: ${theme.spacing(1)};
        padding-right: ${theme.spacing(1)};
        border-radius: ${drHerancaBranding.radius.pill};
        color: ${drHerancaBranding.color.text.primary};
        border: 1px solid ${drHerancaBranding.color.border.default};

        &:hover {
          background: ${drHerancaBranding.color.accent.light};
          border-color: ${drHerancaBranding.color.accent.main};
        }
`
);

const MenuUserBox = styled(Box)(
  ({ theme }) => `
        background: ${drHerancaBranding.color.surface.subtle};
        padding: ${theme.spacing(2)};
`
);

const UserBoxText = styled(Box)(
  ({ theme }) => `
        text-align: left;
        padding-left: ${theme.spacing(1)};
`
);

const UserBoxLabel = styled(Typography)(
  () => `
        font-weight: ${drHerancaBranding.fontWeight.bold};
        color: ${drHerancaBranding.color.text.primary};
        display: block;
`
);

const UserBoxDescription = styled(Typography)(
  () => `
        color: ${drHerancaBranding.color.text.secondary}
`
);

function HeaderUserbox() {
  const config = useConfig();

  // Don't render if no user configuration is provided
  if (!config.user?.useUserInfo) {
    return null;
  }

  // Get user info using tenant-provided hook
  // Hook is always called when config exists (React hooks rules)
  const { data, isLoading } = config.user.useUserInfo();

  const ref = useRef<any>(null);
  const [isOpen, setOpen] = useState<boolean>(false);

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const logout = async (): Promise<void> => {
    await performLogout();
  };

  const BooksCreateAvatar = (props) => {
    if (isLoading) return (<></>)
    if (!isLoading && !data) {
      logout();
    }
    if (!data?.user) return (<></>);
    return (
      <Avatar alt={data.user.name}>
        {data.user.name.substring(0, 1)}
      </Avatar>
    );
  };

  const BooksCreateAvatarSummary = (props) => {
    if (isLoading || !data?.user) return (<></>)
      return (
      <UserBoxText>
        <UserBoxLabel variant="body1">{data.user.name}</UserBoxLabel>
        {data.user.userName && (
          <UserBoxDescription variant="body2">
            {data.user.userName}
          </UserBoxDescription>
        )}
      </UserBoxText>
    );
  };

  return (
    <>
      <UserBoxButton color="secondary" ref={ref} onClick={handleOpen}>
        <BooksCreateAvatar />
        <Hidden mdDown>
          <BooksCreateAvatarSummary />
        </Hidden>
        <Hidden smDown>
          <ExpandMoreTwoToneIcon sx={{ ml: 1 }} />
        </Hidden>
      </UserBoxButton>
      <Popover
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: drHerancaBranding.radius.card,
            border: `1px solid ${drHerancaBranding.color.border.default}`,
            boxShadow: 'none',
          },
        }}
      >
        <MenuUserBox sx={{ minWidth: 210 }} display="flex">
          <BooksCreateAvatar />
          <BooksCreateAvatarSummary />
        </MenuUserBox>
        <Divider sx={{ mb: 0 }} />
        <List sx={{ p: 1 }} component="nav">
          {config.user?.menuItems?.map((item, index) => {
            const Icon = item.icon || PersonIcon;
            const link = typeof item.link === 'function' ? item.link(data) : item.link;
            return (
              <ListItem
                key={index}
                button
                to={link}
                component={NavLink}
                onClick={handleClose}
                sx={{
                  borderRadius: '10px',
                  color: drHerancaBranding.color.text.menu,
                  textDecoration: 'none',
                  '&:visited': {
                    color: drHerancaBranding.color.text.menu,
                  },
                  '& .MuiListItemText-primary': {
                    color: drHerancaBranding.color.text.menu,
                  },
                  '& .MuiSvgIcon-root': {
                    color: drHerancaBranding.color.accent.main,
                  },
                  '&.active': {
                    bgcolor: drHerancaBranding.color.surface.icon,
                    color: drHerancaBranding.color.text.primary,
                    '& .MuiListItemText-primary': {
                      color: drHerancaBranding.color.text.primary,
                    },
                  },
                  '&:hover': {
                    bgcolor: drHerancaBranding.color.accent.light,
                  },
                }}
              >
                <Icon fontSize="small" />
                <ListItemText primary={item.name} />
              </ListItem>
            );
          })}
        </List>
        <Divider />
        <Box sx={{ m: 1 }}>
          <Button
            fullWidth
            onClick={() => { logout(); handleClose(); }}
            sx={drHerancaBrandingPresets.buttonOutlined}
          >
            <LockOpenTwoToneIcon sx={{ mr: 1 }} />
            Sair
          </Button>
        </Box>
      </Popover>
    </>
  );
}

export default HeaderUserbox;

